import { faUser, faUserSlash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Container, Grid, Tooltip, Typography } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApiGet } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import ClpTable from "components/ClpTable"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { MultiSelectFilter } from "components/ClpTable/ClpTableFilterTypes"
import { StyledBoldTypography } from "components/ClpTable/components/typography/typography.styled"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import theme from "css/theme/light"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { allRoutes } from "generated/routes"
import { useContext, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link, useHistory } from "react-router-dom"
import { StyledTableContainer } from "shared/styled/Global"

const truncate = (str) => {
  return str.length > 39 ? str.substring(0, 40) + "..." : str
}

const EditCustomerUsers = () => {
  const routerHistory = useHistory()
  const [usersResult, getUsers] = useApiGet()
  const loadingContext = useContext(GlobalLoaderContext)
  const { t } = useTranslation()

  const appContext = useContext(AppContext)
  const isLimitedManager = hasRole(appContext.appUser, allRoles.CustomerAdminLimitedManagerAccess)

  useEffect(() => {
    getUsers(apiEndpoints.customeradmin.getcustomerusers)
  }, [getUsers])

  useEffect(() => {
    loadingContext.setLoading(() => usersResult.status === 1)
  })

  const columns = [
    {
      accessorFn: (row) => (row.disabled ? "disabled" : "active"),
      filterFn: "arrIncludesSome",
      cell: ({ row }) => {
        const label = row.original.disabled ? (
          <FontAwesomeIcon color={theme.palette.error["main"]} icon={faUserSlash} />
        ) : (
          <FontAwesomeIcon color={theme.palette.primary[700]} icon={faUser} style={{ paddingLeft: "2px" }} />
        )
        return <>{label}</>
      },
      header: t("global.status", "Status"),
      meta: {
        defaultValue: ["active"],
        filterHeader: t("global.status", "Status"),
        mainFilter: true,
        filter: MultiSelectFilter,
        filterOptions: [
          { value: "active", name: t("global.active_users", "Active users") },
          {
            value: "disabled",
            name: t("global.disabled_users", "Deactivated users")
          }
        ]
      }
    },
    {
      accessorKey: "name",
      header: t("global.name", "Name"),
      cell: ({ row }) => (
        <>
          <StyledBoldTypography>{row.original.name}</StyledBoldTypography>
        </>
      )
    },
    {
      accessorKey: "username",
      header: t("global.user_name", "User name")
    },
    {
      accessorKey: "email",
      header: t("global.email", "Email")
    },
    {
      accessorFn: (row) => row?.companies?.join(", "),
      header: t("global.company", "Company"),
      cell: ({ row }) => (
        <>
          <Tooltip title={row.original.companies.join(", ")}>
            <Typography variant="caption">{truncate(row.original.companies.join(", "))}</Typography>
          </Tooltip>
        </>
      )
    },
    {
      accessorFn: (row) => row?.departments?.join(", "),
      header: t("global.departments", "Department"),
      cell: ({ row }) => (
        <>
          <Tooltip title={row.original.departments.join(", ")}>
            <Typography variant="caption">{truncate(row.original.departments.join(", "))}</Typography>
          </Tooltip>
        </>
      )
    },
    {
      accessorKey: "manager.name",
      header: t("global.manager", "Manager")
    }
  ]

  const initialFilters = [
    {
      id: "disabled",
      value: "active"
    }
  ]

  return (
    <div>
      <Container>
        <Box>
          <Grid container direction="row" justifyContent="space-between" alignItems="flex-end" gap={1} rowGap={2}>
            <Box display="flex" flexDirection="column">
              <Typography variant="h1">{t("global.users", "Users")}</Typography>
              {isLimitedManager && (
                <Box>
                  <Typography variant="bodySmall" sx={{ whiteSpace: "normal" }}>
                    {t(
                      "settings.user.the_list_shows_users",
                      "The list only shows users who have you as their manager."
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
            <Button color="primary" component={Link} to={allRoutes.CustomerSettingsUsersAdd()}>
              {t("settings.user.add_user", "Add user")}
            </Button>
          </Grid>
        </Box>
      </Container>
      <StyledTableContainer>
        <ClpTable
          columns={columns}
          onRowClick={(row) => routerHistory.push(allRoutes.CustomerSettingsUsersEdit(row.original.id))}
          rows={usersResult.status === 2 && usersResult.data}
          loadingState={usersResult.status}
          initialFilters={initialFilters}
          tableId={ClpTableEnum.SettingsUsersRegistry}
        />

      </StyledTableContainer>
    </div>
  )
}
export default EditCustomerUsers
